import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, Circle, PlayCircle, Map, Award } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { useRoadmapProgress } from "@/hooks/useRoadmapProgress";
import { AuthModal } from "@/components/AuthModal";
import { StreakBadges } from "@/components/StreakBadges";
import { RoadmapCalendar } from "@/components/RoadmapCalendar";
import { TimeTracker } from "@/components/TimeTracker";
import { ReminderSettings } from "@/components/ReminderSettings";
import { NotesExporter } from "@/components/NotesExporter";
import { CertificateModal } from "@/components/CertificateModal";
import confetti from "canvas-confetti";
import {
  RoadmapWeek,
  getTotalDays,
  getDayKey,
  getLastUncompletedDay,
  getAllDaysFlat,
  normalizeRoadmapTo28Days,
} from "@/lib/roadmapHelpers";
import { calculateStreak } from "@/lib/streakHelpers";
import { cn } from "@/lib/utils";
import { PremiumUnlockGate } from "@/components/PremiumUnlockGate";
import { useUnlock } from "@/hooks/useUnlock";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
}

const RoadmapOverview = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapWeek[]>([]);
  const [roadmapId, setRoadmapId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [hasShownConfetti, setHasShownConfetti] = useState(false);

  const totalDays = getTotalDays(roadmap);
  const allDays = useMemo(() => getAllDaysFlat(roadmap), [roadmap]);
  const { isUnlocked, loading: unlockLoading } = useUnlock(user, "premium_guide", post?.id);

  const { completedTasks, isDayCompleted, progressPercentage } = useRoadmapProgress({
    roadmapId,
    user,
    totalDays,
  });

  const lastUncompletedDay = useMemo(
    () => getLastUncompletedDay(roadmap, completedTasks),
    [roadmap, completedTasks]
  );

  // Calculate streak
  const streakData = useMemo(() => {
    return calculateStreak(completedTasks, totalDays);
  }, [completedTasks, totalDays]);

  // Show confetti when user completes 100%
  useEffect(() => {
    if (progressPercentage === 100 && !hasShownConfetti && user) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      setHasShownConfetti(true);
    }
  }, [progressPercentage, hasShownConfetti, user]);

  useEffect(() => {
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });

      return () => subscription.unsubscribe();
    };

    initAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      try {
        setLoading(true);

        // Fetch blog post
        const { data: postData, error: postError } = await supabase
          .from("blog_posts")
          .select("id, title, slug")
          .eq("slug", slug)
          .eq("status", "published")
          .single();

        if (postError) throw postError;
        setPost(postData);

        // Fetch roadmap
        const { data: roadmapData, error: roadmapError } = await supabase
          .from("roadmaps")
          .select("id, content")
          .eq("blog_post_id", postData.id)
          .maybeSingle();

        if (roadmapError) throw roadmapError;

        if (roadmapData) {
          setRoadmapId(roadmapData.id);
          const rawRoadmap = roadmapData.content as unknown as RoadmapWeek[];
          // Normalize to exactly 28 days with sequential numbering (1-28)
          const normalizedRoadmap = normalizeRoadmapTo28Days(rawRoadmap);
          setRoadmap(normalizedRoadmap);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load roadmap",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, toast]);

  const handleDayClick = (dayNumber: number) => {
    navigate(`/blog/${slug}/roadmap/day/${dayNumber}`);
  };

  const handleContinue = () => {
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    handleDayClick(lastUncompletedDay);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <p className="text-muted-foreground">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const completedCount = Object.values(completedTasks).filter(Boolean).length;

  return (
    <>
      <Helmet>
        <title>28-Day Learning Roadmap - {post?.title}</title>
        <meta
          name="description"
          content={`Complete 28-day learning roadmap for ${post?.title}. Track your progress day by day.`}
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />

        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <Link
              to={`/blog/${slug}`}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Article
            </Link>

            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Map className="h-6 w-6 text-primary" />
                  <h1 className="text-3xl md:text-4xl font-bold">28-Day Learning Roadmap</h1>
                </div>
                <p className="text-muted-foreground">{post?.title}</p>
              </div>
            </div>

            {post && (
              <PremiumUnlockGate
                purchaseType="premium_guide"
                entityId={post.id}
                title={`Unlock the 28-Day Roadmap for "${post.title}"`}
                description="The full roadmap, daily tasks, progress tracking, streaks, time tracker, and certificate are all part of the Premium Guide for this article."
                perks={[
                  "Full 28-Day Roadmap with daily tasks & checkboxes",
                  "Progress tracking, streaks & completion certificate",
                  "All downloadable resources for this article included",
                  "Yours forever — one-time $3 unlock",
                ]}
              >
            {/* Progress Card */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium mb-1">Overall Progress</p>
                    <p className="text-2xl font-bold">{progressPercentage}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      {completedCount} of {totalDays} days completed
                    </p>
                  </div>
                </div>
                <Progress value={progressPercentage} className="h-3 mb-4" />

                {user ? (
                  <Button onClick={handleContinue} className="w-full" size="lg">
                    <PlayCircle className="h-5 w-5 mr-2" />
                    {completedCount === 0
                      ? "Start Day 1"
                      : completedCount === totalDays
                      ? `Review Day ${totalDays}`
                      : `Continue from Day ${lastUncompletedDay}`}
                  </Button>
                ) : (
                  <Button onClick={() => setAuthModalOpen(true)} className="w-full" size="lg">
                    Sign In to Track Progress
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Streak Display */}
            {user && streakData.currentStreak > 0 && (
              <div className="mb-6">
                <StreakBadges
                  currentStreak={streakData.currentStreak}
                  longestStreak={streakData.longestStreak}
                />
              </div>
            )}

            {/* Time Tracker */}
            {user && (
              <TimeTracker
                roadmap={roadmap}
                completedTasks={completedTasks}
                className="mb-6"
              />
            )}

            {/* Certificate Card - Show when 100% complete */}
            {user && progressPercentage === 100 && (
              <Card className="mb-6 border-primary bg-gradient-to-br from-primary/5 to-background">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Award className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-1">
                          Congratulations! 🎉
                        </h3>
                        <p className="text-muted-foreground">
                          You've completed the entire 28-day learning roadmap. Download your certificate of completion!
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setCertificateModalOpen(true)}
                      size="lg"
                      className="shrink-0"
                    >
                      <Award className="mr-2 h-5 w-5" />
                      Get Certificate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Calendar View */}
            <RoadmapCalendar
              roadmap={roadmap}
              isDayCompleted={isDayCompleted}
              currentDayNumber={undefined}
              onDayClick={handleDayClick}
              className="mb-8"
            />

            {/* Settings and Export */}
            {user && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <ReminderSettings
                  roadmapId={roadmapId}
                  user={user}
                  onSignInRequired={() => setAuthModalOpen(true)}
                />
                <NotesExporter
                  roadmapId={roadmapId}
                  user={user}
                  onSignInRequired={() => setAuthModalOpen(true)}
                />
              </div>
            )}

          {/* Days Grid by Week */}
          <div className="space-y-8">
            {roadmap.map((week) => (
              <div key={week.week}>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold mb-1">
                    Week {week.week}: {week.title}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {week.days.filter((day) =>
                      isDayCompleted(getDayKey(week.week, day.day))
                    ).length}{" "}
                    of {week.days.length} days completed
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {week.days.map((day) => {
                    const dayKey = getDayKey(week.week, day.day);
                    const isCompleted = isDayCompleted(dayKey);
                    const absoluteDay = ((week.week - 1) * 7) + day.day;

                    return (
                      <Card
                        key={dayKey}
                        className={cn(
                          "cursor-pointer transition-all hover:shadow-lg hover:scale-105",
                          isCompleted && "border-green-500 bg-green-500/5"
                        )}
                        onClick={() => handleDayClick(absoluteDay)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant={isCompleted ? "default" : "outline"}>
                              Day {absoluteDay}
                            </Badge>
                            {isCompleted ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <Circle className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <h3
                            className={cn(
                              "font-medium text-sm line-clamp-2",
                              isCompleted && "text-muted-foreground"
                            )}
                          >
                            {day.title}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-2">
                            {day.tasks.length} tasks
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
              </PremiumUnlockGate>
            )}
          </div>
        </div>

        <Footer />
      </div>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />

      {user && roadmapId && post && (
        <CertificateModal
          open={certificateModalOpen}
          onOpenChange={setCertificateModalOpen}
          blogPostTitle={post.title}
          roadmapId={roadmapId}
          userId={user.id}
        />
      )}
    </>
  );
};

export default RoadmapOverview;
